import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, RotateCcw, Clapperboard, Globe, Upload, Loader2, Camera, Mic, Wand2, ImagePlus, Music, Lock, UserRound, Captions, Info, ChevronDown, ChevronRight, Sparkles, Square } from 'lucide-react';
import { KIND_META, TIER_VALUE } from './nodes';
import { UGC_SCRIPTS } from './nodes';
import GeneratorControls from './GeneratorControls';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { api, fileUrl } from '../../lib/api';
import { downscaleToDataUrl } from '../../lib/img';
import { useEntitlements, TIER_NAMES } from '../../lib/entitlements';
import { toast } from '../../hooks/use-toast';

// The generator has no physical common sense: a blue jar of moisturiser got it
// painting blue cream onto a cheek. Naming the category attaches the one rule
// that category gets wrong, so the packaging colour stops leaking into the
// product itself.
function CategoryPicker({ assetId }) {
  const [cats, setCats] = useState([]);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.productCategories().then((c) => { if (alive) setCats(c); }).catch(() => {});
    api.listAssets().then((rows) => {
      const a = (rows || []).find((x) => x.id === assetId);
      if (alive && a) setValue(a.category || '');
    }).catch(() => {});
    return () => { alive = false; };
  }, [assetId]);

  const pick = async (next) => {
    setValue(next);
    setSaving(true);
    try {
      await api.patchAsset(assetId, { category: next });
      toast({ title: next ? `Category set to ${next}` : 'Category cleared' });
    } catch (e) {
      toast({ title: 'Could not save the category', description: e.message });
    } finally { setSaving(false); }
  };

  const rule = cats.find((c) => c.key === value)?.rule;

  return (
    <Row label="Category">
      <select className={selCls} value={value} disabled={saving}
        data-testid="asset-category" onChange={(e) => pick(e.target.value)}>
        <option value="">None</option>
        {cats.map((c) => <option key={c.key} value={c.key}>{c.key}</option>)}
      </select>
      <p className="mt-1 text-[10px] leading-relaxed text-white/40">
        {rule || 'Tells the generator how this kind of product physically behaves, so the packaging colour does not end up on the product itself.'}
      </p>
    </Row>
  );
}

function LockCard({ title, tier }) {
  return (
    <div className="rounded-lg border border-dashed border-[#2A2A30] bg-[#141417] p-3 text-center" data-testid={`lock-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <Lock size={16} className="mx-auto text-white/40" />
      <p className="mt-1 text-xs font-medium text-white/80">{title}</p>
      <p className="mt-0.5 text-[10px] text-node-model">🔒 Unlock with {TIER_NAMES[tier] || 'an upgrade'}</p>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">{label}</label>
    {children}
  </div>
);

const selCls = 'w-full rounded-md border border-[#2A2A30] bg-[#141417] px-2.5 py-2 text-sm text-white focus:border-white/30 focus:outline-none';

// El parrafo explicativo largo, entero, detras de un ⓘ. El control queda a la
// vista y la prosa a un clic, sin perder ni una palabra.
function InfoDot({ children, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white/40 hover:text-white"
          title="More about this">
          <Info size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start"
        className="w-72 border-[#2A2A30] bg-[#141417] p-3 text-[11px] leading-relaxed text-white/70">
        {children}
      </PopoverContent>
    </Popover>
  );
}

// Secciones plegadas por defecto (Prompt, Advanced, Log): el panel mostraba
// ocho bloques abiertos a la vez y ninguno cabia. El estado se recuerda.
const RP_OPEN_KEY = 'idolaris_rightpanel_open';

function readRpOpen() {
  try { return JSON.parse(localStorage.getItem(RP_OPEN_KEY) || '{}'); } catch { return {}; }
}

function CollapseSection({ id, title, badge, open, onToggle, preview, children }) {
  const isOpen = !!open[id];
  return (
    <div className="rounded-lg border border-[#2A2A30] bg-[#141417]">
      <button
        onClick={() => onToggle(id)}
        data-testid={`rp-section-${id}`}
        className="flex w-full items-center gap-1.5 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-white/50 transition-colors hover:text-white/80"
      >
        {isOpen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
        <span className="truncate">{title}</span>
        {badge}
      </button>
      {!isOpen && preview && <div className="px-3 pb-2.5">{preview}</div>}
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function VoiceOverEditor({ d, set, influencer }) {
  const { has } = useEntitlements();
  const [script, setScript] = useState(d.voScript || '');
  const [voice, setVoice] = useState(d.voVoice || influencer?.voice || 'nova');
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [audioUrl, setAudioUrl] = useState(d.voAudioUrl ? fileUrl(d.voAudioUrl) : null);
  const [musicName, setMusicName] = useState(d.voMusicName || null);
  const [musicVol, setMusicVol] = useState(d.voMusicVolume ?? 0.18);
  const [musicBusy, setMusicBusy] = useState(false);
  const [subtitles, setSubtitles] = useState(d.voSubtitles ?? true);
  const musicRef = useRef(null);
  // Six voices on one model was the whole offering, and it read Spanish with a
  // Latin American accent that nothing could change. The catalogue comes from
  // the API so the list is only ever models this account can actually reach.
  const [ttsModels, setTtsModels] = useState([]);
  const [ttsModel, setTtsModel] = useState(d.voTtsModel || '');
  const [ttsInstr, setTtsInstr] = useState(d.voTtsInstructions || '');
  useEffect(() => {
    api.voiceCatalogue().then((rows) => {
      setTtsModels(rows);
      if (!d.voTtsModel && rows.length) { setTtsModel(rows[0].id); set({ voTtsModel: rows[0].id }); }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const currentModel = ttsModels.find((m) => m.id === ttsModel) || ttsModels[0];
  const voices = currentModel?.voices || ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
  const lang = influencer?.language || 'Spanish';

  // Auto-adopt the influencer's saved voice for a spoken UGC (unless the user already picked one).
  useEffect(() => {
    if (influencer?.voice && !d.voVoice) { setVoice(influencer.voice); set({ voVoice: influencer.voice }); }
    // eslint-disable-next-line
  }, [influencer?.id]);

  const draft = async () => {
    setDrafting(true);
    try {
      const context = influencer
        ? `${influencer.name}${influencer.persona ? `, ${influencer.persona}` : ''}, speaking to camera about ${d.prompt || 'this product'}`
        : (d.prompt || 'this product review');
      const r = await api.voiceoverScript({ context, seconds: d.duration || 8, language: lang });
      setScript(r.script); set({ voScript: r.script });
    } catch (e) { toast({ title: 'Draft failed', description: e.message }); }
    setDrafting(false);
  };

  const onMusicPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setMusicBusy(true);
      try {
        const r = await api.uploadAudio({ dataUrl: reader.result });
        setMusicName(f.name);
        set({ voMusicKey: r.key, voMusicName: f.name });
        toast({ title: 'Music added', description: 'It will play softly under the narration.' });
      } catch (err) { toast({ title: 'Music upload failed', description: err.message }); }
      setMusicBusy(false);
    };
    reader.readAsDataURL(f);
  };

  const removeMusic = () => { setMusicName(null); set({ voMusicKey: null, voMusicName: null }); };

  if (!has('audio.voice')) return <LockCard title="Voice-over" tier="oto1" />;

  const generate = async () => {
    if (!script.trim()) { toast({ title: 'Write or draft a script first' }); return; }
    setBusy(true);
    try {
      const r = await api.voiceover({ text: script, voice, ttsModel, ttsInstructions: ttsInstr, generationId: d.resultVideoGenerationId || null, musicKey: d.voMusicKey || null, musicVolume: musicVol, subtitles });
      setAudioUrl(fileUrl(r.audioUrl));
      const patch = { voScript: script, voVoice: voice, voTtsModel: ttsModel, voTtsInstructions: ttsInstr, voAudioUrl: r.audioUrl, voMusicVolume: musicVol, voSubtitles: subtitles };
      if (r.videoUrl) {
        patch.resultVideoUrl = r.videoUrl; patch.resultVideoGenerationId = r.generationId;
        toast({ title: 'Voice-over merged into the video' });
      } else {
        toast({ title: 'Voice-over audio ready', description: 'Generate the video first, then click Voice to merge it onto the clip.' });
      }
      set(patch);
    } catch (e) { toast({ title: 'Voice-over failed', description: e.message }); }
    setBusy(false);
  };

  return (
    <div className="space-y-2.5 rounded-lg border border-[#2A2A30] bg-[#141417] p-3">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-node-style"><Mic size={12} /> Voice-over</p>
        <button onClick={draft} disabled={drafting} data-testid="voiceover-draft" className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white">
          {drafting ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} Draft with AI
        </button>
      </div>
      {influencer && (
        <div className="flex items-center gap-1.5 rounded-md border border-node-model/30 bg-node-model/5 px-2 py-1 text-[10px] text-node-model" data-testid="voiceover-influencer-badge">
          <UserRound size={11} /> Speaking as <span className="font-semibold">{influencer.name}</span> · voice {influencer.voice} · {lang}
        </div>
      )}
      <textarea rows={3} value={script} onChange={(e) => { setScript(e.target.value); set({ voScript: e.target.value }); }}
        placeholder="Narration script… or draft with AI" data-testid="voiceover-script"
        className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2 text-xs leading-relaxed text-white/85 focus:border-node-style focus:outline-none" />
      <div className="flex items-center gap-2">
        <select value={ttsModel} data-testid="voiceover-model" className={selCls}
          onChange={(e) => {
            const next = e.target.value;
            const m = ttsModels.find((x) => x.id === next);
            const firstVoice = m?.voices?.[0] || 'alloy';
            setTtsModel(next); setVoice(firstVoice);
            set({ voTtsModel: next, voVoice: firstVoice });
          }}>
          {ttsModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        {currentModel?.note && <p className="text-[10px] leading-relaxed text-white/40">{currentModel.note}</p>}
        {currentModel?.instructions && (
          <input value={ttsInstr} data-testid="voiceover-instructions"
            onChange={(e) => { setTtsInstr(e.target.value); set({ voTtsInstructions: e.target.value }); }}
            placeholder="Accent and delivery, e.g. neutral Castilian Spanish, warm and unhurried"
            className={selCls} />
        )}
        <select value={voice} onChange={(e) => { setVoice(e.target.value); set({ voVoice: e.target.value }); }} data-testid="voiceover-voice" className={selCls}>
          {voices.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button onClick={generate} disabled={busy} data-testid="voiceover-generate"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-node-style/50 bg-node-style/10 px-3 py-2 text-xs font-medium text-node-style transition-colors hover:bg-node-style/20 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Mic size={13} />} Voice
        </button>
      </div>
      {has('audio.music') ? (
      <div className="rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2.5">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[11px] font-medium text-white/70"><Music size={12} className="text-node-model" /> Background music</p>
          {musicName ? (
            <button onClick={removeMusic} data-testid="music-remove" className="inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-red-400"><Trash2 size={11} /> remove</button>
          ) : (
            <button onClick={() => musicRef.current?.click()} disabled={musicBusy} data-testid="music-upload" className="inline-flex items-center gap-1 text-[10px] text-white/60 hover:text-white">
              {musicBusy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} upload mp3
            </button>
          )}
          <input ref={musicRef} type="file" accept="audio/*" className="hidden" onChange={onMusicPick} />
        </div>
        {musicName && <p className="mt-1 truncate text-[10px] text-white/45">{musicName}</p>}
        <div className="mt-2">
          <label className="text-[10px] text-white/50">Music volume · {Math.round(musicVol * 100)}%</label>
          <input type="range" min={0} max={0.5} step={0.02} value={musicVol}
            onChange={(e) => { const v = parseFloat(e.target.value); setMusicVol(v); set({ voMusicVolume: v }); }}
            data-testid="music-volume" className="mt-1 w-full accent-node-model" />
        </div>
      </div>
      ) : <LockCard title="Background music" tier="oto1" />}
      <button
        type="button"
        role="switch"
        aria-checked={subtitles}
        onClick={() => { const v = !subtitles; setSubtitles(v); set({ voSubtitles: v }); }}
        data-testid="voiceover-subtitles"
        className={`flex w-full items-center justify-between rounded-md border px-2.5 py-2 text-left transition-colors ${subtitles ? 'border-node-style/50 bg-node-style/10' : 'border-[#2A2A30] bg-[#0E0E10]'}`}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-white/80"><Captions size={13} className={subtitles ? 'text-node-style' : 'text-white/50'} /> Burn-in subtitles</span>
        <span className={`flex h-4 w-7 items-center rounded-full px-0.5 transition-colors ${subtitles ? 'justify-end bg-node-style' : 'justify-start bg-white/15'}`}><span className="h-3 w-3 rounded-full bg-white" /></span>
      </button>
      {audioUrl && <audio src={audioUrl} controls className="w-full" data-testid="voiceover-audio" />}
      <p className="text-[10px] leading-relaxed text-white/40">Generate the video first, then add the voice-over. It merges (with your music{subtitles ? ' & subtitles' : ''}) onto the clip. Subtitles are perfect for silent social feeds.</p>
    </div>
  );
}

function WebsiteEditor({ d, set }) {
  const [url, setUrl] = useState(d.url || '');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(d.captureMode || 'desktop');
  const fileRef = useRef(null);

  const capture = async () => {
    const u = url.trim();
    if (!u) { toast({ title: 'Enter a URL first' }); return; }
    setBusy(true);
    try {
      const a = await api.screenshotWebsite({ url: u, mode });
      set({ url: u, assetId: a.id, thumb: a.url, captureMode: mode });
      toast({ title: 'Website captured', description: 'Screenshot ready. Press Generate.' });
    } catch (e) {
      toast({ title: 'Capture failed', description: e.message || 'Try uploading a screenshot image instead.' });
    }
    setBusy(false);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        const a = await api.createAsset({ type: 'website', name: file.name.replace(/\.[^.]+$/, ''), dataUrl: reader.result });
        set({ assetId: a.id, thumb: a.url, url: url.trim() });
        toast({ title: 'Screenshot uploaded' });
      } catch (err) { toast({ title: 'Upload failed', description: err.message }); }
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const sceneMode = d.sceneMode || 'influencer';
  return (
    <>
      <Row label="Website URL">
        <div className="flex gap-2">
          <input className={selCls} value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="acme.com" data-testid="website-url-input" />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {[{ k: 'desktop', l: 'Desktop' }, { k: 'fullpage', l: 'Full page' }, { k: 'mobile', l: 'Mobile' }].map((o) => (
            <button key={o.k} onClick={() => { setMode(o.k); set({ captureMode: o.k }); }} data-testid={`website-mode-${o.k}`}
              className={`rounded-md border px-2 py-1.5 text-[11px] transition-colors ${mode === o.k ? 'border-node-style bg-node-style/15 text-white' : 'border-[#2A2A30] bg-[#141417] text-white/60 hover:border-white/25'}`}>
              {o.l}
            </button>
          ))}
        </div>
        <button onClick={capture} disabled={busy} data-testid="website-capture-btn"
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-node-style/50 bg-node-style/10 py-2 text-sm font-medium text-node-style transition-colors hover:bg-node-style/20 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Capture screenshot
        </button>
      </Row>
      <Row label="Or upload a screenshot">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="website-upload-btn"
          className="flex w-full items-center justify-center gap-2 rounded-md border border-[#2A2A30] bg-[#141417] py-2 text-sm text-white/80 transition-colors hover:border-white/30 disabled:opacity-50">
          <Upload size={14} /> Upload image
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
      </Row>
      {d.thumb && (
        <div className="overflow-hidden rounded-md border border-[#2A2A30]">
          <img src={fileUrl(d.thumb)} alt="" className="w-full object-cover object-top" />
        </div>
      )}
      <Row label="Review scene">
        <div className="grid grid-cols-2 gap-2">
          {[{ k: 'influencer', l: 'Influencer' }, { k: 'screen', l: 'On-screen' }].map((o) => (
            <button key={o.k} onClick={() => set({ sceneMode: o.k })} data-testid={`website-scene-${o.k}`}
              className={`rounded-md border px-2 py-2 text-xs transition-colors ${sceneMode === o.k ? 'border-node-style bg-node-style/15 text-white' : 'border-[#2A2A30] bg-[#141417] text-white/60 hover:border-white/25'}`}>
              {o.l}
            </button>
          ))}
        </div>
      </Row>
      <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-2.5 text-[11px] leading-relaxed text-white/45">
        Capture or upload the site, wire this into the <span className="text-node-generator">Image Generator</span>, then chain a <span className="text-node-model">Video Generator</span> for the review clip.
      </div>
    </>
  );
}

function RevealNodeUploader({ d, set }) {
  const ref = useRef(null);
  const reveal = d.revealImageDataUrl || null;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { set({ revealImageDataUrl: await downscaleToDataUrl(f) }); } catch { /* ignore */ }
  };
  return (
    <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-node-model"><ImagePlus size={12} /> Reveal image · optional</p>
      <div className="flex items-center gap-3">
        {reveal ? (
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[#2A2A30]">
            <img src={reveal} alt="" className="h-full w-full object-cover" />
            <button onClick={() => set({ revealImageDataUrl: null })} data-testid="reveal-node-remove" className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-red-400"><Trash2 size={11} /></button>
          </div>
        ) : (
          <button onClick={() => ref.current?.click()} data-testid="reveal-node-upload" className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 transition-colors hover:border-node-model hover:text-node-model">
            <ImagePlus size={16} /><span className="text-[9px]">Add</span>
          </button>
        )}
        <p className="text-[11px] leading-relaxed text-white/45">Attach what's <span className="text-white/70">inside the box</span> so the AI reveals the real product. The starting frame is recomposited before the video renders.</p>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
    </div>
  );
}

// A generation that failed used to leave nothing behind but a red icon on the
// node. The reason was already in the record -- which model, how long it took,
// and the provider's own words about why it stopped -- so this puts it where
// the person deciding what to change is already looking.
function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

const RUN_TONE = {
  succeeded: 'text-emerald-400',
  failed: 'text-red-400',
  running: 'text-amber-300',
  queued: 'text-white/50',
};

function RunLog({ runs, scope }) {
  if (!runs.length) {
    return <p className="text-[11px] leading-relaxed text-white/35">Nothing has been run yet. Attempts show up here with the model that made them and, when one fails, what the provider said.</p>;
  }
  return (
    <div className="space-y-1.5">
      {scope === 'workflow' && (
        <p className="text-[10px] text-white/30">Nothing from this node yet — showing the rest of the workflow.</p>
      )}
      <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
        {runs.map((g) => (
          <div key={g.id} className="rounded-md border border-[#2A2A30] bg-[#141417] p-2">
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <span className={`font-medium ${RUN_TONE[g.status] || 'text-white/50'}`}>{g.status}</span>
              <span className="text-white/30">{ago(g.created_at)}</span>
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-2 text-[10px] text-white/40">
              <span className="truncate">{(g.model_slug || '—').split('/').pop()}</span>
              <span className="shrink-0">
                {g.provider ? `${g.provider} · ` : ''}
                {g.duration_ms ? `${(g.duration_ms / 1000).toFixed(1)}s` : ''}
              </span>
            </div>
            {g.error_message && (
              <p className="mt-1.5 select-text text-[11px] leading-relaxed text-red-300/85">{g.error_message}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Improve reads whatever is in the box: the creator's own sentence when they
// have written one, otherwise the prompt the canvas compiled. Either way the
// result becomes theirs, and Auto is still one click away. Its own component
// because RightPanel returns early when nothing is selected and holds no state
// of its own, so a hook in there would fire conditionally.
function PromptTools({ current, onApply }) {
  const [improving, setImproving] = useState(false);
  const run = async () => {
    const text = (current || '').trim();
    if (!text) {
      toast({ title: 'Nothing to improve yet',
              description: 'Connect a subject to compile a prompt, or write one yourself.' });
      return;
    }
    setImproving(true);
    try {
      const r = await api.improvePrompt({ prompt: text });
      onApply(r.prompt);
      toast({ title: 'Prompt improved',
              description: `${r.before} → ${r.after} characters. Press Auto to go back to the canvas version.` });
    } catch (e) {
      toast({ title: 'Could not improve it', description: e.message });
    } finally {
      setImproving(false);
    }
  };
  return (
    <button onClick={run} disabled={improving}
            data-testid="prompt-improve"
            title="Rewrite this prompt, keeping the face lock and the other constraints"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-node-style/40 px-1.5 py-0.5 text-[11px] text-node-style hover:bg-node-style/10 disabled:opacity-40">
      {improving ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
      {improving ? 'Improving…' : 'Improve'}
    </button>
  );
}

export default function RightPanel({ node, onChange, onRemove, compiled, compiling, onMakeVideo, influencer, generations = [], onGenerate, onStop, generating, genCost }) {
  // Plegado por defecto: Prompt, Advanced y Log arrancan cerrados y el estado
  // se recuerda. Los hooks van antes del early return para no dispararse
  // condicionalmente.
  const [rpOpen, setRpOpen] = useState(readRpOpen);
  const toggleRp = useCallback((id) => {
    setRpOpen((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(RP_OPEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Barra fija al fondo del panel: Generate y su coste siempre a la vista,
  // sin scroll. Vive aqui y no en la top bar para que el precio este al lado
  // de lo que lo decide (shots x tier).
  const stickyBar = onGenerate ? (
    <div className="sticky bottom-0 mt-auto shrink-0 border-t border-[#2A2A30] bg-[#0E0E10] p-3">
      <div className="flex items-center gap-2">
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="rp-generate-btn"
          className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-white text-sm font-medium text-black transition-transform hover:scale-[1.02] disabled:opacity-50"
        >
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {generating ? 'Generating…' : (genCost ? `Generate · ${genCost} credits` : 'Generate')}
        </button>
        {generating && onStop && (
          <button onClick={onStop} data-testid="rp-stop-btn"
            className="inline-flex h-10 items-center gap-1.5 rounded-full border border-red-500/50 bg-red-500/10 px-3.5 text-sm font-medium text-red-300 transition-colors hover:bg-red-500/20">
            <Square size={13} className="fill-current" /> Stop
          </button>
        )}
      </div>
    </div>
  ) : null;

  if (!node) {
    return (
      <aside className="hidden h-full w-80 shrink-0 flex-col border-l border-[#2A2A30] bg-[#0E0E10] lg:flex">
        <p className="p-5 text-sm text-white/40">Select a node to edit its properties.</p>
        {stickyBar}
      </aside>
    );
  }
  const d = node.data || {};
  const kind = d.kind;
  const meta = KIND_META[kind] || {};
  const set = (patch) => onChange(node.id, patch);
  // This node's own attempts. A workflow can hold more than one generator, so
  // showing all of them here would answer a question nobody asked; when this
  // one has never run, the rest of the workflow is better than an empty box.
  // Grok is text-to-image and this studio is built on references, so the two
  // can never meet. Read off the compile the panel already has.
  const blockedByRefs = !!compiled?.hasReferences;
  // Manual, and no longer what the canvas would say. Only flagged once the
  // compile has arrived, so it never blinks on while that is in flight.
  const promptIsStale = typeof d.promptOverride === 'string' && d.promptOverride.trim()
    && !!compiled?.prompt && compiled.prompt.trim() !== d.promptOverride.trim();
  const stills = (generations || []).filter((g) => g && g.kind !== 'video');
  const mine = stills.filter((g) => g.node_id === node.id);
  const nodeRuns = (mine.length ? mine : stills).slice(0, 12);
  const nodeRunScope = mine.length ? 'node' : 'workflow';

  return (
    <aside className="hidden h-full w-80 shrink-0 flex-col overflow-y-auto border-l border-[#2A2A30] bg-[#0E0E10] no-scrollbar lg:flex">
      <div className="flex items-center justify-between border-b border-[#2A2A30] p-4">
        <span className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</span>
        <button onClick={() => onRemove(node.id)} className="flex h-7 w-7 items-center justify-center rounded-md text-white/40 hover:bg-white/5 hover:text-red-400">
          <Trash2 size={14} />
        </button>
      </div>

      <div className="flex-1 space-y-4 p-4">
        {(kind === 'product' || kind === 'model' || kind === 'environment') && (
          <Row label="Name">
            {/* Renaming here used to relabel the canvas node only, leaving the
                library record on its uploaded filename. They are kept in step:
                the asset name is what the script writer says out loud. */}
            <input className={selCls} value={d.name || ''}
              onChange={(e) => set({ name: e.target.value })}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (d.assetId && next) {
                  api.patchAsset(d.assetId, { name: next })
                    .then(() => toast({ title: 'Renamed', description: next }))
                    .catch((err) => toast({ title: 'Could not rename the asset', description: err.message }));
                }
              }} />
          </Row>
        )}

        {kind === 'product' && d.assetId && <CategoryPicker assetId={d.assetId} />}

        {kind === 'website' && <WebsiteEditor d={d} set={set} />}

        {kind === 'influencer' && (
          <div className="space-y-3">
            {d.thumb && (
              <div className="overflow-hidden rounded-lg border border-[#2A2A30]">
                <img src={fileUrl(d.thumb)} alt="" className="aspect-square w-full object-cover" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-white">{d.name || 'Influencer'}</p>
              {d.persona && <p className="mt-1 text-xs leading-relaxed text-white/55">{d.persona}</p>}
            </div>
            <div className="rounded-lg border border-node-model/30 bg-node-model/5 p-2.5 text-[11px] leading-relaxed text-node-model/90">
              This influencer's reference angles are injected into the generator so the <span className="font-semibold">same face</span> appears across every image. Wire it into an <span className="text-node-generator">Image Generator</span>.
            </div>
            <p className="text-[10px] leading-relaxed text-white/40">Manage the identity, voice and consistency angles from the AI Influencers page.</p>
          </div>
        )}

        {kind === 'style' && (
          <Row label={`Intensity · ${Math.round((d.intensity ?? 0.8) * 100)}%`}>
            <input type="range" min={0} max={1} step={0.1} value={d.intensity ?? 0.8}
              onChange={(e) => set({ intensity: parseFloat(e.target.value) })}
              className="w-full accent-node-style" />
          </Row>
        )}

        {kind === 'framing' && (
          <>
            <Row label="Aspect ratio">
              <select className={selCls} value={d.aspect} onChange={(e) => set({ aspect: e.target.value })}>
                {['1:1', '4:5', '3:2', '9:16', '16:9'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
            <Row label="Shot">
              <select className={selCls} value={d.shot} onChange={(e) => set({ shot: e.target.value })}>
                {['close-up', 'three-quarter', 'full-body', 'wide'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
            <Row label="Crop">
              <select className={selCls} value={d.crop} onChange={(e) => set({ crop: e.target.value })}>
                {['waist-up', 'chest-up', 'full', 'detail'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
          </>
        )}

        {kind === 'lighting' && (
          <Row label="Preset">
            <select className={selCls} value={d.preset} onChange={(e) => set({ preset: e.target.value })}>
              {['soft directional', 'softbox', 'golden hour', 'hard flash', 'overcast', 'neon'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
        )}

        {kind === 'camera' && (
          <>
            <Row label="Lens (mm)">
              <select className={selCls} value={d.lens} onChange={(e) => set({ lens: parseInt(e.target.value) })}>
                {[24, 35, 50, 85, 135].map((o) => <option key={o} value={o}>{o}mm</option>)}
              </select>
            </Row>
            <Row label="Aperture">
              <select className={selCls} value={d.aperture} onChange={(e) => set({ aperture: e.target.value })}>
                {['f/1.4', 'f/2.0', 'f/2.8', 'f/4', 'f/5.6', 'f/8'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
            <Row label="Angle">
              <select className={selCls} value={d.angle} onChange={(e) => set({ angle: e.target.value })}>
                {['eye-level', 'low', 'high', 'top-down'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
          </>
        )}

        {kind === 'mood' && (
          <Row label="Preset">
            <select className={selCls} value={d.preset} onChange={(e) => set({ preset: e.target.value })}>
              {['calm', 'energetic', 'luxurious', 'playful', 'austere'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
        )}

        {kind === 'color' && (
          <Row label="Colour grade">
            <select className={selCls} value={d.preset} onChange={(e) => set({ preset: e.target.value })}>
              {['warm', 'cool', 'muted', 'vibrant', 'monochrome', 'teal & orange', 'pastel', 'sepia', 'high saturation'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
        )}

        {kind === 'composition' && (
          <Row label="Composition">
            <select className={selCls} value={d.preset} onChange={(e) => set({ preset: e.target.value })}>
              {['rule of thirds', 'centred', 'symmetrical', 'negative space', 'tight crop', 'dutch angle', 'flat lay', 'leading lines'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
        )}

        {kind === 'expression' && (
          <Row label="Expression">
            <select className={selCls} value={d.preset || 'warm genuine smile'}
              data-testid="expression-preset" onChange={(e) => set({ preset: e.target.value })}>
              {['warm genuine smile', 'confident neutral, direct gaze', 'delighted surprise, eyebrows raised',
                'mid-laugh, eyes creased', 'focused and attentive, slight frown of concentration',
                'soft thoughtful half smile', 'sceptical, one eyebrow raised',
                'relieved, tension leaving the face', 'excited, mouth slightly open',
                'calm and reassuring, steady eye contact'].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <p className="mt-1 text-[10px] leading-relaxed text-white/40">The face is what sells the shot, and nothing else in the graph controls it.</p>
          </Row>
        )}

        {kind === 'outfit' && (
          <Row label="Outfit">
            <textarea rows={3} value={d.text || ''} onChange={(e) => set({ text: e.target.value })}
              placeholder="e.g. an oversized cream knit sweater and straight-leg jeans…"
              className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-sm text-white/85 focus:border-white/30 focus:outline-none" />
            <p className="mt-1 text-[10px] text-white/40">Replaces the clothing from the reference images. The face and identity stay locked.</p>
          </Row>
        )}

        {kind === 'custom' && (
          <Row label="Custom instruction">
            <textarea rows={3} value={d.text || ''} onChange={(e) => set({ text: e.target.value })}
              placeholder="e.g. holding a coffee cup, seen from behind…"
              className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-sm text-white/85 focus:border-white/30 focus:outline-none" />
          </Row>
        )}

        {kind === 'generator' && (
          <>
            {/* Siempre visibles: los controles compartidos con el modo Simple.
                Shots, Shape y Quality leen y escriben el mismo nodo del grafo. */}
            <GeneratorControls d={d} set={set} compiled={compiled} />
            {/* A different renderer, not a different quality. Swimwear, lingerie,
                a horror costume, bare skin in a skincare ad: all ordinary
                advertising, all turned down at some point by the models that
                lead this chain. This one says yes to them. */}
            <Row label="Fewer refusals">
              <div className="flex items-start gap-2">
                <label className={`flex flex-1 items-start gap-2 ${blockedByRefs ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={!!d.permissive && !blockedByRefs}
                    disabled={blockedByRefs}
                    onChange={(e) => set({ permissive: e.target.checked })}
                    data-testid="generator-permissive"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-node-style"
                  />
                  <span className="text-[11px] leading-relaxed text-white/60">
                    Render on Grok Imagine instead.
                  </span>
                </label>
                {/* El parrafo entero vive en el ⓘ; nada se pierde, solo deja de
                    ocupar media pantalla. */}
                <InfoDot testid="permissive-info">
                  {blockedByRefs ? (
                    <>
                      Not available here. Grok Imagine renders from the words alone and
                      cannot look at the reference images wired into this generator, so it
                      would invent a different person and a different product rather than
                      use yours. If something is being blocked, it is usually one
                      reference picture rather than the prompt: a garment photographed on
                      a bare body is the one that catches most often, and the same garment
                      flat, on a hanger or on a mannequin normally goes straight through.
                    </>
                  ) : (
                    <>
                      Use it when a perfectly normal advert keeps getting refused —
                      swimwear, lingerie, a costume, skin in a skincare shot.
                      It renders from the text alone, so it cannot use an influencer or a
                      product photo wired into this generator. Leave it off unless
                      something is actually being blocked. Not for explicit images of a
                      real person: this studio keeps a real face across renders, and that
                      combination is illegal in most places and will lose you the account
                      you sell through.
                    </>
                  )}
                </InfoDot>
              </div>
              {blockedByRefs && (
                <p className="mt-1 text-[10px] leading-relaxed text-amber-400/80">
                  Not available with reference images wired in — see ⓘ.
                </p>
              )}
            </Row>
            <CollapseSection
              id="prompt"
              open={rpOpen}
              onToggle={toggleRp}
              title={<>Prompt {d.promptOverride != null ? <span className="normal-case tracking-normal text-node-product">· yours</span> : <span className="normal-case tracking-normal text-white/40">· auto</span>} {compiling && <span className="normal-case tracking-normal text-white/40">· compiling…</span>}</>}
              preview={(
                <p className="line-clamp-2 text-[11px] leading-relaxed text-white/45">
                  {(d.promptOverride != null ? d.promptOverride : (compiled?.prompt || '')) || 'Connect a subject to compile a prompt, or write your own.'}
                </p>
              )}
            >
              <div className="mb-2 flex items-center justify-end gap-1">
                {/* Three states, two buttons. Auto is the canvas compiled;
                    typing makes it yours; Improve works on whichever of the two
                    is in the box. */}
                {d.promptOverride != null && (
                  <button onClick={() => set({ promptOverride: null })}
                          data-testid="prompt-auto"
                          title="Go back to the prompt the canvas compiles"
                          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-white/50 hover:bg-white/5 hover:text-white">
                    <RotateCcw size={11} /> Auto
                  </button>
                )}
                <PromptTools
                  current={d.promptOverride != null ? d.promptOverride : (compiled?.prompt || '')}
                  onApply={(p) => set({ promptOverride: p })}
                />
              </div>
              {/* A hand written prompt wins over the canvas, which is the point
                  of it and also the trap: swap the product and the picture changes
                  while the words keep describing the old one. Said out loud, with
                  the way back one click away. */}
              {promptIsStale && (
                <div className="mb-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2">
                  <p className="text-[11px] leading-relaxed text-amber-300/90">
                    This prompt was written by hand, so it no longer matches the canvas.
                    The reference images still update when you swap a node, but these
                    words do not.
                  </p>
                  <button onClick={() => set({ promptOverride: null })}
                          data-testid="prompt-use-canvas"
                          className="mt-1.5 text-[11px] font-medium text-amber-300 underline underline-offset-2 hover:text-amber-200">
                    Use the canvas version instead
                  </button>
                </div>
              )}
              {compiled?.error ? (
                <p className="text-xs leading-relaxed text-amber-400">{compiled.error}</p>
              ) : (
                <>
                  <textarea
                    rows={6}
                    value={d.promptOverride != null ? d.promptOverride : (compiled?.prompt || '')}
                    onChange={(e) => set({ promptOverride: e.target.value })}
                    placeholder="Connect a subject to compile a prompt, or write your own…"
                    className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2 text-xs leading-relaxed text-white/85 focus:border-node-style focus:outline-none"
                  />
                  {/* What this generator is actually reading. A canvas can end
                      up with the old product still wired beside the new one, and
                      then the prompt names both -- which reads as "it did not
                      change" until you can see the pair. */}
                  {!!compiled?.inputs?.length && (
                    <div className="mt-2 border-t border-[#2A2A30] pt-2">
                      <p className="mb-1 text-[10px] uppercase tracking-wider text-white/35">Reading</p>
                      <div className="flex flex-wrap gap-1">
                        {compiled.inputs.map((i, k) => {
                          const dupe = compiled.inputs.filter((x) => x.type === i.type).length > 1;
                          return (
                            <span key={`${i.type}-${k}`}
                                  title={dupe ? `Two ${i.type} nodes are wired in, so both are in the prompt` : i.type}
                                  className={`rounded-full border px-2 py-0.5 text-[10px] ${
                                    dupe ? 'border-amber-500/40 bg-amber-500/10 text-amber-300/90'
                                         : 'border-[#2A2A30] text-white/45'}`}>
                              {i.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-[#2A2A30] pt-2 text-[11px] text-white/40">
                    <span>{compiled?.model?.split('/').pop() || '-'}</span>
                    <span>~{compiled?.estimatedCredits ?? '?'} credits</span>
                  </div>
                </>
              )}
            </CollapseSection>
            <CollapseSection id="advanced" title="Advanced" open={rpOpen} onToggle={toggleRp}>
              <Row label="Negative prompt">
                <textarea
                  rows={2}
                  value={d.negativeOverride != null ? d.negativeOverride : (compiled?.negative || '')}
                  onChange={(e) => set({ negativeOverride: e.target.value })}
                  placeholder="What to avoid…"
                  className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-xs leading-relaxed text-white/70 focus:border-white/30 focus:outline-none"
                />
              </Row>
            </CollapseSection>
            <CollapseSection id="log" title="Log" open={rpOpen} onToggle={toggleRp}>
              <RunLog runs={nodeRuns} scope={nodeRunScope} />
            </CollapseSection>
            {/* onMakeVideo is withheld entirely while video is switched off, so
                the row disappears rather than sitting there greyed out. */}
            {onMakeVideo && d.resultUrl && d.resultGenerationId && (
              <button
                onClick={() => onMakeVideo({ id: d.resultGenerationId, url: d.resultUrl })}
                data-testid="create-ugc-video-btn"
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-node-model/50 bg-node-model/10 py-2.5 text-sm font-medium text-node-model transition-colors hover:bg-node-model/20"
              >
                <Clapperboard size={15} /> Create UGC video
              </button>
            )}
          </>
        )}

        {kind === 'combine' && (
          <>
            <Row label="Relation between items">
              <textarea rows={3} value={d.relation || ''} onChange={(e) => set({ relation: e.target.value })}
                placeholder="e.g. the sneaker resting inside the open shoebox with tissue paper"
                className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-sm text-white/85 focus:border-node-model focus:outline-none" />
            </Row>
            <Row label="Quick presets">
              <div className="flex flex-wrap gap-1.5">
                {[
                  { l: 'Open box (visible)', t: 'the product sitting inside an open cardboard box with the flaps folded open, the product clearly visible from a top-down angle, unboxing composition' },
                  { l: 'Lifted out', t: 'a hand lifting the product out of an open cardboard box, the product fully visible and presented to camera' },
                  { l: 'Held in hand', t: 'the product held in a hand, presented to camera' },
                  { l: 'Next to', t: 'the two items arranged neatly next to each other, both clearly visible' },
                  { l: 'Worn by model', t: 'the product worn by the model' },
                  { l: 'Stacked', t: 'the items stacked on top of each other' },
                ].map((p) => (
                  <button key={p.l} onClick={() => set({ relation: p.t })}
                    className="rounded-full border border-[#2A2A30] bg-[#141417] px-2.5 py-1 text-[11px] text-white/70 transition-colors hover:border-node-model hover:text-white">
                    {p.l}
                  </button>
                ))}
              </div>
            </Row>
            <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-2.5 text-[11px] leading-relaxed text-white/45">
              Connect 2-4 <span className="text-node-product">Subjects</span> into this node, then wire its output into the <span className="text-node-generator">Image Generator</span>.
            </div>
            <div className="rounded-lg border border-node-model/30 bg-node-model/5 p-2.5 text-[11px] leading-relaxed text-node-model/90">
              For unboxing videos, keep the box <span className="font-semibold">open</span> and the product <span className="font-semibold">visible</span>, the video can only animate what already appears in the image.
            </div>
          </>
        )}

        {kind === 'video' && (
          <>
            {/* Tier, not model. The renderer is picked from this plus the
                length plus whoever has credit, so a model name here would be
                a guess. Workflows saved with the old veo3 values still load:
                the backend maps them onto the ends of this scale. */}
            <Row label="Quality">
              <select className={selCls} value={TIER_VALUE(d.model)} onChange={(e) => set({ model: e.target.value })}>
                <option value="fast">Fast · quickest and cheapest</option>
                <option value="standard">Standard · balanced</option>
                <option value="premium">Premium · best quality</option>
              </select>
            </Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label={`Duration · ${d.duration || 8}s`}>
                <input type="range" min={4} max={8} step={2} value={d.duration || 8} onChange={(e) => set({ duration: parseInt(e.target.value) })} className="w-full accent-node-model" />
              </Row>
              <Row label="Aspect">
                <select className={selCls} value={d.aspect || '9:16'} onChange={(e) => set({ aspect: e.target.value })}>
                  {['9:16', '16:9', '1:1'].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Row>
            </div>
            <Row label="UGC script">
              <div className="flex flex-wrap gap-1.5">
                {UGC_SCRIPTS.map((s) => (
                  <button key={s.key} onClick={() => set({ prompt: s.text })}
                    className="rounded-full border border-[#2A2A30] bg-[#141417] px-2.5 py-1 text-[11px] text-white/70 transition-colors hover:border-node-model hover:text-white">
                    {s.label}
                  </button>
                ))}
              </div>
            </Row>
            <Row label="Motion / script">
              <textarea rows={4} value={d.prompt || ''} onChange={(e) => set({ prompt: e.target.value })}
                placeholder="Describe the motion, or pick a UGC script above…"
                className="w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-sm text-white/85 focus:border-node-model focus:outline-none" />
            </Row>
            <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-2.5 text-[11px] leading-relaxed text-white/45">
              Connect an <span className="text-node-generator">Image Generator</span> <span className="text-white/60">or</span> an <span className="text-node-model">Influencer</span>, then press <span className="text-white">Generate</span>. If the photo is not made yet it renders first, then the clip · 320 credits (+ the photo's cost when one is rendered)
            </div>
            <RevealNodeUploader d={d} set={set} />
            <VoiceOverEditor d={d} set={set} influencer={influencer} />
          </>
        )}
      </div>
      {stickyBar}
    </aside>
  );
}
